import { useEffect, useState, useMemo } from "react";
import { fmtDate, fmtDateTime, fmtH, fmtKc } from "../lib/format";
import { PayModeBadge } from "../lib/payMode";
import { weekLabel, weekStart, weekKey, parseWeekKey, addDays } from "../lib/period";
import { OrphanRecordsPanel } from "../components/OrphanRecordsPanel";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Plus, Trash2, Search, HardHat, X, Save, ChevronLeft, Edit3, Briefcase, ChevronDown, ChevronRight, User } from "lucide-react";
import { toast } from "sonner";
import { JOB_STATUSES } from "../lib/jobStatus";
import { useAuth } from "../context/AuthContext";
import { isTech } from "../lib/roles";

const fmtHrs = fmtH;

const JOB_STATUS = Object.fromEntries(JOB_STATUSES.map((s) => [s.key, { label: s.label, color: s.badge }]));

const EMPTY_ENTRY = {
    craftsman_id: "",
    name: "",
    rate_per_hour: 0,
    hours: 0,
    advance: 0,
    invoiced_amount: 0,
    job_id: "",
    job_title: "",
    date: new Date().toISOString().slice(0, 10),
    notes: "",
};

export default function Craftsmen() {
    const { jobId } = useParams();
    return jobId ? <JobCraftsmenDetail jobId={jobId} /> : <JobsOverview />;
}

// ============================================================
// LEVEL 1: JOBS OVERVIEW — list of jobs with craftsmen stats
// ============================================================
function JobsOverview() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [period, setPeriod] = useState(() => localStorage.getItem("craftsmen_period") || "week");   // week | all
    const [week, setWeek] = useState(() => weekKey(weekStart(new Date())));
    const navigate = useNavigate();

    const load = () => api.get("/craftsmen/jobs-summary", { params: period === "week" ? { week } : {} }).then((r) => { setRows(r.data); setLoading(false); });

    useEffect(() => { load(); localStorage.setItem("craftsmen_period", period); }, [period, week]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {}); }, []);
    const periodLabel = period === "week" ? weekLabel(parseWeekKey(week)) : "Celkem za celou dobu";
    const thisWeek = weekKey(weekStart(new Date()));

    const save = async () => {
        if (!editing.name?.trim()) { toast.error("Vyplňte jméno řemeslníka"); return; }
        if (!editing.job_id) { toast.error("Vyberte zakázku"); return; }
        try {
            const payload = {
                ...editing,
                craftsman_id: editing.craftsman_id || null,
                rate_per_hour: Number(editing.rate_per_hour) || 0,
                hours: Number(editing.hours) || 0,
                advance: Number(editing.advance) || 0,
                invoiced_amount: Number(editing.invoiced_amount) || 0,
            };
            await api.post("/craftsmen", payload);
            toast.success("Uloženo");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Uložení selhalo");
        }
    };

    const filtered = useMemo(
        () => rows.filter((s) => !q || (s.job_title + " " + (s.client_name || "")).toLowerCase().includes(q.toLowerCase())),
        [rows, q]
    );

    const grand = useMemo(() => rows.reduce((acc, s) => ({
        hours: acc.hours + s.total_hours,
        amount: acc.amount + s.total_amount,
        advance: acc.advance + s.total_advance,
        invoiced: acc.invoiced + s.total_invoiced,
    }), { hours: 0, amount: 0, advance: 0, invoiced: 0 }), [rows]);

    return (
        <div className="px-10 py-10 max-w-7xl mx-auto animate-fade-in" data-testid="craftsmen-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Evidence dle zakázek</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Řemeslníci</h1>
                    <p className="text-base md:text-lg font-semibold text-slate-900 mt-2" data-testid="craftsmen-period-label">{periodLabel}</p>
                    <p className="text-sm text-slate-600 mt-1">Vyberte zakázku a uvidíte, kdo na ní pracoval, kolik hodin a kolik dostal záloh. <Link to="/remeslnici/dochazka" className="text-orange-700 underline" data-testid="link-attendance">Docházka</Link></p>
                </div>
                {!isTech(user) && <Link to="/remeslnici/vyplaty" data-testid="link-payroll" className="inline-flex items-center gap-2 border border-slate-900 px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-slate-900 hover:text-white rounded-sm mr-2">Výplaty</Link>}
                <button onClick={() => setEditing({ ...EMPTY_ENTRY })} data-testid="new-craftsman-entry-button"
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                    <Plus className="w-4 h-4" /> Nový záznam
                </button>
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="craftsmen-period-nav">
                <div className="inline-flex border border-slate-900 rounded-sm overflow-hidden" role="tablist">
                    <button onClick={() => setPeriod("week")} data-testid="period-week" className={`px-4 min-h-[40px] text-xs uppercase tracking-wider font-semibold ${period === "week" ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}>Tento týden</button>
                    <button onClick={() => setPeriod("all")} data-testid="period-all" className={`px-4 min-h-[40px] text-xs uppercase tracking-wider font-semibold border-l border-slate-900 ${period === "all" ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}>Celkem</button>
                </div>
                {period === "week" && (
                    <div className="inline-flex items-center gap-2">
                        <button onClick={() => setWeek(weekKey(addDays(parseWeekKey(week), -7)))} data-testid="week-prev" aria-label="Předchozí týden" className="min-h-[40px] min-w-[40px] inline-flex items-center justify-center border border-slate-300 hover:bg-slate-100 rounded-sm"><ChevronLeft className="w-4 h-4" /></button>
                        <button onClick={() => setWeek(weekKey(addDays(parseWeekKey(week), 7)))} data-testid="week-next" aria-label="Další týden" className="min-h-[40px] min-w-[40px] inline-flex items-center justify-center border border-slate-300 hover:bg-slate-100 rounded-sm"><ChevronRight className="w-4 h-4" /></button>
                        <button onClick={() => setWeek(thisWeek)} disabled={week === thisWeek} data-testid="week-today" className="min-h-[40px] px-3 text-xs uppercase tracking-wider font-semibold border border-slate-300 hover:bg-slate-100 rounded-sm disabled:opacity-40">Aktuální týden</button>
                    </div>
                )}
                <span className="text-xs text-slate-500" data-testid="craftsmen-period-hint">Karty i tabulka: {period === "week" ? "jen záznamy v tomto týdnu" : "všechny záznamy od začátku"}</span>
            </div>

            {!isTech(user) && <OrphanRecordsPanel onChanged={load} />}

            {rows.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                    <StatCard label="Hodiny celkem" value={fmtHrs(grand.hours)} testid="grand-hours" />
                    <StatCard label="Odpracováno" value={fmtKc(grand.amount)} testid="grand-amount" />
                    <StatCard label="Vyplaceno záloh" value={fmtKc(grand.advance)} testid="grand-advance" />
                    <StatCard label="Fakturováno klientům" value={fmtKc(grand.invoiced)} testid="grand-invoiced" gold />
                </div>
            )}

            <div className="bg-white border border-slate-200">
                <div className="flex items-center px-4 py-3 border-b border-slate-200 gap-3">
                    <Search className="w-4 h-4 text-slate-400" />
                    <input placeholder="Hledat zakázku…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="craftsmen-search"
                           className="flex-1 text-sm focus:outline-none bg-transparent" />
                    <span className="text-xs text-slate-400">{filtered.length} / {rows.length}</span>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-slate-400 text-xs uppercase tracking-widest">Načítání…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-16 text-center">
                        <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
                        <div className="font-display text-lg font-bold text-slate-700">
                            {rows.length === 0 ? (period === "week" ? "V tomto týdnu nejsou žádné záznamy" : "Žádné zakázky s řemeslníky") : "Nic nenalezeno"}
                        </div>
                        <p className="text-sm text-slate-500 mt-1 mb-6">
                            {rows.length === 0 ? "Přidejte první záznam — vyberte zakázku, jméno řemeslníka, hodiny." : "Zkuste jiné hledání."}
                        </p>
                        {rows.length === 0 && (
                            <button onClick={() => setEditing({ ...EMPTY_ENTRY })} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                                <Plus className="w-4 h-4" /> Přidat záznam
                            </button>
                        )}
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b-2 border-slate-900">
                                <th className="px-4 py-3 font-semibold">Zakázka</th>
                                <th className="px-4 py-3 font-semibold">Stav</th>
                                <th className="px-4 py-3 font-semibold text-right">Řemeslníků</th>
                                <th className="px-4 py-3 font-semibold text-right">Hodin</th>
                                <th className="px-4 py-3 font-semibold text-right">Odpracováno</th>
                                <th className="px-4 py-3 font-semibold text-right">Zálohy</th>
                                <th className="px-4 py-3 font-semibold text-right">Doplatit</th>
                                <th className="px-4 py-3 font-semibold text-right">Fakturace</th>
                            </tr>
                        </thead>
                        <tbody className="tabular-data">
                            {filtered.map((s, i) => {
                                const st = JOB_STATUS[s.job_status] || null;
                                return (
                                    <tr key={s.job_id || `_none_${i}`}
                                        onClick={() => s.job_id && navigate(`/remeslnici/zakazka/${s.job_id}`)}
                                        data-testid={`job-row-${s.job_id || "none"}`}
                                        className={`border-b border-slate-100 cursor-pointer hover:bg-orange-50/60 ${i % 2 ? "bg-slate-50/40" : ""}`}>
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-slate-900 hover:text-orange-700 inline-flex items-center gap-2">{s.job_title} {s.pay_mode === "na_klic" && <PayModeBadge mode="na_klic" />}</div>
                                            <div className="text-xs text-slate-500">
                                                {s.client_name && <span>{s.client_name} · </span>}
                                                {s.craftsmen_count} {s.craftsmen_count === 1 ? "řemeslník" : (s.craftsmen_count < 5 ? "řemeslníci" : "řemeslníků")}
                                                {" · "}{s.entry_count} {s.entry_count === 1 ? "záznam" : (s.entry_count < 5 ? "záznamy" : "záznamů")}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {st ? (
                                                <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${st.color}`}>{st.label}</span>
                                            ) : <span className="text-slate-400 text-xs">—</span>}
                                        </td>
                                        <td className="px-4 py-3 text-right text-slate-600">{s.craftsmen_count}</td>
                                        <td className="px-4 py-3 text-right">{fmtHrs(s.total_hours)}</td>
                                        <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmtKc(s.total_amount)}</td>
                                        <td className="px-4 py-3 text-right text-slate-600">{fmtKc(s.total_advance)}</td>
                                        <td className={`px-4 py-3 text-right font-semibold ${s.balance_to_pay > 0 ? "text-red-700" : "text-emerald-700"}`}>
                                            {fmtKc(s.balance_to_pay)}
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-orange-700">{fmtKc(s.total_invoiced)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <EntryModal editing={editing} setEditing={setEditing} save={save} jobs={jobs} />
        </div>
    );
}

// ============================================================
// LEVEL 2: JOB DETAIL — craftsmen for a specific job
// ============================================================
function JobCraftsmenDetail({ jobId }) {
    const [data, setData] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [editing, setEditing] = useState(null);
    const [expandedName, setExpandedName] = useState(null);

    const load = () => api.get(`/craftsmen/by-job/${jobId}`).then((r) => setData(r.data));

    useEffect(() => {
        load();
        api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
    }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

    const save = async () => {
        if (!editing.name?.trim()) { toast.error("Vyplňte jméno řemeslníka"); return; }
        if (!editing.job_id) { toast.error("Vyberte zakázku"); return; }
        try {
            const payload = {
                ...editing,
                craftsman_id: editing.craftsman_id || null,
                rate_per_hour: Number(editing.rate_per_hour) || 0,
                hours: Number(editing.hours) || 0,
                advance: Number(editing.advance) || 0,
                invoiced_amount: Number(editing.invoiced_amount) || 0,
            };
            if (editing.id) await api.put(`/craftsmen/${editing.id}`, payload);
            else await api.post("/craftsmen", payload);
            toast.success("Uloženo");
            setEditing(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
    };

    const del = async (id) => {
        if (!window.confirm("Opravdu smazat tento záznam?")) return;
        try {
            await api.delete(`/craftsmen/${id}`);
            toast.success("Smazáno");
            load();
        } catch { toast.error("Smazání selhalo"); }
    };

    const startEdit = (e) => {
        setEditing({
            id: e.id,
            craftsman_id: e.craftsman_id || "",
            name: e.name,
            rate_per_hour: e.rate_per_hour,
            hours: e.hours,
            advance: e.advance,
            invoiced_amount: e.invoiced_amount,
            job_id: e.job_id || jobId,
            job_title: e.job_title || data?.job?.title || "",
            date: e.date,
            notes: e.notes || "",
        });
    };

    if (!data) {
        return <div className="px-10 py-10 text-center text-slate-400 text-xs uppercase tracking-widest">Načítání…</div>;
    }

    const job = data.job;
    const jobStatus = job && JOB_STATUS[job.status];

    return (
        <div className="px-10 py-10 max-w-7xl mx-auto animate-fade-in" data-testid="job-craftsmen-page">
            <Link to="/remeslnici" data-testid="back-to-jobs"
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-4">
                <ChevronLeft className="w-3.5 h-3.5" /> Zpět na zakázky
            </Link>
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Zakázka</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900" data-testid="job-title">
                        {job?.title || "(neznámá zakázka)"}
                    </h1>
                    <div className="text-sm text-slate-600 mt-2 flex items-center gap-3 flex-wrap">
                        {job?.client_name && <span><span className="text-slate-400">Klient:</span> {job.client_name}</span>}
                        {job?.site_address && <span className="text-slate-500">· {job.site_address}</span>}
                        {jobStatus && <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${jobStatus.color}`}>{jobStatus.label}</span>}
                        {job?.id && (
                            <Link to={`/zakazky/${job.id}`} className="text-orange-700 hover:underline text-xs uppercase tracking-widest font-bold">
                                Otevřít zakázku →
                            </Link>
                        )}
                    </div>
                </div>
                <button onClick={() => setEditing({ ...EMPTY_ENTRY, job_id: jobId, job_title: job?.title || "" })}
                        data-testid="add-entry-for-job"
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                    <Plus className="w-4 h-4" /> Přidat řemeslníka
                </button>
            </div>

            {/* Job-level totals */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
                <StatCard label="Hodin celkem" value={fmtHrs(data.totals.total_hours)} testid="stat-hours" />
                <StatCard label="Odpracováno (Kč)" value={fmtKc(data.totals.total_amount)} testid="stat-amount" />
                <StatCard label="Zálohy vyplaceno" value={fmtKc(data.totals.total_advance)} testid="stat-advance" />
                <StatCard label="Zbývá doplatit" value={fmtKc(data.totals.balance_to_pay)} testid="stat-balance"
                          highlight={data.totals.balance_to_pay > 0} />
                <StatCard label="Fakturováno klientovi" value={fmtKc(data.totals.total_invoiced)} testid="stat-invoiced" gold />
            </div>

            <div className="bg-white border border-slate-200">
                {data.craftsmen.length === 0 ? (
                    <div className="p-16 text-center">
                        <HardHat className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
                        <div className="font-display text-lg font-bold text-slate-700">Zatím žádný řemeslník</div>
                        <p className="text-sm text-slate-500 mt-1 mb-6">Přidejte první záznam k této zakázce.</p>
                        <button onClick={() => setEditing({ ...EMPTY_ENTRY, job_id: jobId, job_title: job?.title || "" })}
                                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                            <Plus className="w-4 h-4" /> Přidat řemeslníka
                        </button>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-200">
                        {data.craftsmen.map((c) => {
                            const isOpen = expandedName === c.name_key;
                            return (
                                <div key={c.name_key} data-testid={`craftsman-block-${c.name_key}`}>
                                    <button onClick={() => setExpandedName(isOpen ? null : c.name_key)}
                                            data-testid={`toggle-craftsman-${c.name_key}`}
                                            className="w-full grid grid-cols-12 gap-2 items-center px-4 py-3 hover:bg-orange-50/60 text-left">
                                        <div className="col-span-4 flex items-center gap-2">
                                            {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                                            <User className="w-4 h-4 text-slate-500" />
                                            <div>
                                                <div className="font-semibold text-slate-900">{c.name}</div>
                                                <div className="text-xs text-slate-500">{c.entry_count} {c.entry_count === 1 ? "záznam" : (c.entry_count < 5 ? "záznamy" : "záznamů")}</div>
                                            </div>
                                        </div>
                                        <div className="col-span-1 text-right text-sm">{fmtHrs(c.total_hours)}</div>
                                        <div className="col-span-2 text-right text-sm font-semibold text-slate-900">{fmtKc(c.total_amount)}</div>
                                        <div className="col-span-2 text-right text-sm text-slate-600">Záloha: {fmtKc(c.total_advance)}</div>
                                        <div className={`col-span-1 text-right text-sm font-semibold ${c.balance_to_pay > 0 ? "text-red-700" : "text-emerald-700"}`}>
                                            {fmtKc(c.balance_to_pay)}
                                        </div>
                                        <div className="col-span-2 text-right text-sm font-semibold text-orange-700">Fakt.: {fmtKc(c.total_invoiced)}</div>
                                    </button>
                                    {isOpen && (
                                        <div className="bg-slate-50/60 px-4 pb-3">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-300">
                                                        <th className="px-2 py-2 font-semibold">Datum</th>
                                                        <th className="px-2 py-2 font-semibold text-right">Sazba</th>
                                                        <th className="px-2 py-2 font-semibold text-right">Hodiny</th>
                                                        <th className="px-2 py-2 font-semibold text-right">Částka</th>
                                                        <th className="px-2 py-2 font-semibold text-right">Záloha</th>
                                                        <th className="px-2 py-2 font-semibold text-right">Doplatit</th>
                                                        <th className="px-2 py-2 font-semibold text-right">Fakturace</th>
                                                        <th className="px-2 py-2 font-semibold">Poznámka</th>
                                                        <th className="px-2 py-2"></th>
                                                    </tr>
                                                </thead>
                                                <tbody className="tabular-data">
                                                    {c.entries.map((e) => (
                                                        <tr key={e.id} className="border-b border-slate-100" data-testid={`entry-row-${e.id}`}>
                                                            <td className="px-2 py-2 text-slate-700">{fmtDate(e.date)}</td>
                                                            <td className="px-2 py-2 text-right text-slate-600">{fmtKc(e.rate_per_hour)}/h</td>
                                                            <td className="px-2 py-2 text-right">{fmtHrs(e.hours)}</td>
                                                            <td className="px-2 py-2 text-right font-semibold">{fmtKc(e.amount)}</td>
                                                            <td className="px-2 py-2 text-right">{fmtKc(e.advance)}</td>
                                                            <td className={`px-2 py-2 text-right font-semibold ${e.balance_to_pay > 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtKc(e.balance_to_pay)}</td>
                                                            <td className="px-2 py-2 text-right font-semibold text-orange-700">{fmtKc(e.invoiced_amount)}</td>
                                                            <td className="px-2 py-2 text-xs text-slate-500 max-w-xs truncate">{e.notes || "—"}</td>
                                                            <td className="px-2 py-2 text-right">
                                                                <div className="flex items-center justify-end gap-1">
                                                                    <button onClick={() => startEdit(e)} data-testid={`edit-entry-${e.id}`}
                                                                            className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded-sm">
                                                                        <Edit3 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                    <button onClick={() => del(e.id)} data-testid={`delete-entry-${e.id}`}
                                                                            className="p-1 text-slate-500 hover:text-red-700 hover:bg-red-50 rounded-sm">
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <EntryModal editing={editing} setEditing={setEditing} save={save} jobs={jobs} lockedJobId={jobId} />
        </div>
    );
}

// ============================================================
// SHARED COMPONENTS
// ============================================================

function StatCard({ label, value, testid, gold, highlight }) {
    const border = gold ? "border-orange-600" : highlight ? "border-red-400" : "border-slate-200";
    const valueColor = gold ? "text-orange-700" : highlight ? "text-red-700" : "text-slate-900";
    return (
        <div className={`bg-white border-l-4 ${border} border-t border-r border-b border-slate-200 px-4 py-3`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">{label}</div>
            <div className={`font-display text-xl font-black tracking-tight ${valueColor}`}>{value}</div>
        </div>
    );
}

function EntryModal({ editing, setEditing, save, jobs, lockedJobId }) {
    const [cards, setCards] = useState([]);
    useEffect(() => { api.get("/craftsmen/cards").then((r) => setCards(r.data)).catch(() => {}); }, []);
    if (!editing) return null;
    const amount = (Number(editing.rate_per_hour) || 0) * (Number(editing.hours) || 0);
    const pickCard = (id) => {
        const c = cards.find((x) => x.id === id);
        setEditing({ ...editing, craftsman_id: id, name: c ? c.name : editing.name, rate_per_hour: c ? c.rate_per_hour : editing.rate_per_hour });
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
             data-testid="entry-modal"
             onClick={() => setEditing(null)}>
            <div className="bg-white border border-slate-200 shadow-2xl max-w-2xl w-full rounded-sm" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                    <div>
                        <div className="label-eyebrow mb-1">{editing.id ? "Úprava záznamu" : "Nový záznam"}</div>
                        <h2 className="font-display text-xl font-black tracking-tight text-slate-900">Řemeslník na zakázce</h2>
                    </div>
                    <button onClick={() => setEditing(null)} data-testid="entry-modal-close" className="text-slate-400 hover:text-slate-900">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="px-6 py-5 grid grid-cols-2 gap-4">
                    <Field label="Zakázka *" className="col-span-2">
                        <select value={editing.job_id || ""} disabled={!!lockedJobId}
                                onChange={(e) => {
                                    const j = jobs.find((x) => x.id === e.target.value);
                                    setEditing({ ...editing, job_id: e.target.value, job_title: j ? j.title : "" });
                                }}
                                data-testid="entry-job-select"
                                className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600 disabled:bg-slate-100 disabled:text-slate-500">
                            <option value="">— Vyberte zakázku —</option>
                            {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}{j.client_name ? ` — ${j.client_name}` : ""}</option>)}
                        </select>
                    </Field>
                    <Field label="Řemeslník z karty" className="col-span-2">
                        <select value={editing.craftsman_id || ""} onChange={(e) => pickCard(e.target.value)} data-testid="entry-card-select"
                                className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600">
                            <option value="">— bez karty (volné jméno) —</option>
                            {cards.map((c) => <option key={c.id} value={c.id}>{c.name}{c.specialization ? ` — ${c.specialization}` : ""}</option>)}
                        </select>
                    </Field>
                    <Field label="Jméno řemeslníka *" className="col-span-2">
                        <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} disabled={!!editing.craftsman_id}
                               data-testid="entry-name-input" placeholder="Petr Novák"
                               className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                    </Field>
                    <Field label="Datum">
                        <input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                               data-testid="entry-date-input"
                               className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                    </Field>
                    <div />
                    <Field label="Sazba (Kč/h)">
                        <input type="number" step="1" min="0" value={editing.rate_per_hour}
                               onChange={(e) => setEditing({ ...editing, rate_per_hour: e.target.value })}
                               data-testid="entry-rate-input"
                               className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                    </Field>
                    <Field label="Hodiny">
                        <input type="number" step="0.5" min="0" value={editing.hours}
                               onChange={(e) => setEditing({ ...editing, hours: e.target.value })}
                               data-testid="entry-hours-input"
                               className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                    </Field>
                    <Field label="Částka (auto)" className="col-span-2">
                        <div className="w-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm rounded-sm font-mono font-semibold text-slate-900"
                             data-testid="entry-amount-preview">
                            {fmtKc(amount)}
                        </div>
                    </Field>
                    <Field label="Záloha (vyplaceno řemeslníkovi)">
                        <input type="number" step="1" min="0" value={editing.advance}
                               onChange={(e) => setEditing({ ...editing, advance: e.target.value })}
                               data-testid="entry-advance-input"
                               className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                    </Field>
                    <Field label="Fakturace klientovi (Kč)">
                        <input type="number" step="1" min="0" value={editing.invoiced_amount}
                               onChange={(e) => setEditing({ ...editing, invoiced_amount: e.target.value })}
                               data-testid="entry-invoiced-input"
                               className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                    </Field>
                    <Field label="Poznámka" className="col-span-2">
                        <textarea rows={2} value={editing.notes}
                                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                                  data-testid="entry-notes-input" placeholder="Popis práce, materiál..."
                                  className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600 resize-none" />
                    </Field>
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
                    <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm font-semibold uppercase tracking-wider border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm">
                        Zrušit
                    </button>
                    <button onClick={save} data-testid="entry-save-button"
                            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 text-sm font-semibold uppercase tracking-wider rounded-sm">
                        <Save className="w-4 h-4" /> Uložit
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <div className={className}>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">{label}</label>
            {children}
        </div>
    );
}
